<script setup lang="ts">
/**
 * Change of Registered Office Address (B32105) — SG 일반 프리셋 Studio 패널.
 *   문서 1종: Directors' Resolution (Change of Registered Office Address) — 이사 전원 서명([[Signers]]).
 *   회사 선택 → 회사명·등록번호·이사(서명자) 자동채움 → 새 주소(📍 챗봇 주소폼)·날짜 입력 → Create Task.
 *   e-Filing(ChangeAddressEFiling) 이 쓰는 TASK_DATA / L_TASK_EFILING_DATA 형식(officeAddr 구조체 등)은
 *   기존 패널과 동일하게 저장한다.
 */
import { sgAppliedAddressLine } from '@/features/startTask/preset_task/sg/preset/utils/sgAppliedAddressLine';
import { ref, computed, watch, onMounted, provide } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { provideAgmFill, typeOf } from './final-dividends/cdd/useCddFill';
import { provideAgmSigners, AGM_ENTITY_CD } from './final-dividends/cdd/agmSignersContext';
import ChangeAddressPackageDoc from './change-address/ChangeAddressPackageDoc.vue';
import { docGenApi } from '@/features/AI-documents/api/docGenApi';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { saveEFilingData } from '@/features/startTask/api/taskApi';
import {
  createEmptyOfficeAddr,
  formatOfficeAddressOneLine,
  buildChangeAddressTaskFormData,
  type OfficeAddrModel,
} from '@/features/startTask/preset_task/sg/components/change-address/composables/useFormPayload';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  appliedAddress?: {
    key: string; line?: string; line1?: string; line2?: string; line3?: string;
    town?: string; state?: string; postcode?: string; country?: string; seq?: number;
  } | null;
  editTaskId?: number | null;
  studio?: boolean;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const TASK_CODE = 'B32105';
const ADDRESS_KEY = 'new_office_address';

const router = useRouter();
const fill = provideAgmFill();
const signersCtx = provideAgmSigners();
const taskConfigSteps = ref<string[]>([]);
const lastAutofillCd = ref('');
const autofilledKeys = ref<Set<string>>(new Set());

/** e-Filing 용 구조화 주소. 주소 문자열(new_office_address)과 항상 함께 갱신한다. */
const officeAddr = ref<OfficeAddrModel>(createEmptyOfficeAddr());
/** 마지막으로 구조화 주소에서 만든 한 줄 주소 — 사용자가 문서에서 직접 고쳤는지 판별용. */
let lastFormattedAddress = '';

const entityCd = computed(() =>
  String(props.entityData?.entityCd || props.entityData?.icpCd || props.chatFormData?.entityCd || props.chatFormData?.icpCd || '').trim(),
);
provide(AGM_ENTITY_CD, entityCd);

const effectiveTitle = computed(() => String(props.taskTitle || '').trim());
const studio = computed(() => !!props.studio);
const editMode = computed(() => !!props.editTaskId);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; presetProgress.message = v ? progress.value || 'Creating task…' : ''; });
watch(progress, (m) => { if (creating.value) presetProgress.message = m; });

function emitProgress() {
  const total = fill.totalFields.value || 1;
  emit('progress-change', Math.min(100, Math.round((fill.filledCount.value / total) * 100)));
}
watch([() => fill.filledCount.value, () => fill.totalFields.value], () => emitProgress());

fill.setActivateHandler((key: string) => {
  if (typeOf(key) === 'entity') emit('change-entity');
});
fill.setAddressHandler((key: string) => { emit('request-address-input', key); });

let signerSeq = 0;
function mkSigner(name: string): SignerEntry {
  return { id: `id-sg-ca-${signerSeq++}`, name: String(name || '').trim(), title: 'Director', role: 'director', isManual: true, showTitle: true, _signerLayout: 'box' } as SignerEntry;
}

// ── SG 주소 → 구조화(officeAddr) ─────────────────────────────────────
const t = (v: unknown) => String(v ?? '').trim();

/** 챗봇 SG 주소폼 결과: line = "blk, street, building, #lvl-unit" + postcode. */
function officeAddrFromChat(line: string, postcode: string): OfficeAddrModel {
  const a = createEmptyOfficeAddr();
  const parts = line.split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.length && parts[parts.length - 1].startsWith('#')) {
    const lu = parts.pop()!.replace(/^#/, '');
    const [lvl, unit] = lu.split('-');
    if (unit !== undefined) { a.levelNo = t(lvl); a.unitNo = t(unit); } else { a.unitNo = t(lvl); }
  }
  if (parts.length > 1 && /^\d+[A-Z]?$/i.test(parts[0])) a.blkHseNo = parts.shift()!;
  a.streetName = parts.shift() || '';
  a.buildingName = parts.join(', ');
  a.postalCode = t(postcode);
  a.haveLevelUnit = !!(a.levelNo || a.unitNo);
  return a;
}

/** 문서에서 직접 입력한 한 줄 주소 — "42 CHAI CHEE STREET #10-60 BUILDING Singapore 461042" 형식을 최대한 분해. */
function officeAddrFromText(text: string): OfficeAddrModel {
  const a = createEmptyOfficeAddr();
  let s = t(text);
  const pc = s.match(/(\d{6})(?!\d)/);
  if (pc) a.postalCode = pc[1];
  s = s.replace(/,?\s*singapore\s*\d{6}\b.*$/i, '').replace(/\b\d{6}\b/, '').trim();
  const lu = s.match(/#\s*(\w+)\s*-\s*(\w+)/);
  if (lu) {
    a.levelNo = lu[1]; a.unitNo = lu[2];
    const [before, after] = [s.slice(0, lu.index), s.slice((lu.index || 0) + lu[0].length)];
    s = before.trim().replace(/,$/, '');
    a.buildingName = after.replace(/^[,\s]+/, '').trim();
  }
  const bs = s.match(/^(\d+[A-Z]?)\s+(.+)$/i);
  if (bs) { a.blkHseNo = bs[1]; a.streetName = bs[2].replace(/,$/, '').trim(); } else { a.streetName = s; }
  a.haveLevelUnit = !!(a.levelNo || a.unitNo);
  return a;
}

function setOfficeAddr(a: OfficeAddrModel) {
  officeAddr.value = a;
  lastFormattedAddress = formatOfficeAddressOneLine(a);
  fill.setValue(ADDRESS_KEY, lastFormattedAddress);
  emitProgress();
}

watch(() => props.appliedAddress, (a) => {
  if (!a?.key) return;
  if (a.key !== ADDRESS_KEY && !a.key.endsWith(`/${ADDRESS_KEY}`)) {
    const line = sgAppliedAddressLine(a);
    if (line) { fill.setValue(a.key, line); emitProgress(); }
    return;
  }
  setOfficeAddr(officeAddrFromChat(t(a.line), t(a.postcode)));
}, { deep: true });

// 문서에서 주소를 직접 고친 경우 → 구조화 주소도 다시 분해.
watch(() => fill.values[ADDRESS_KEY], (v) => {
  const s = t(v);
  if (s && s !== lastFormattedAddress) {
    officeAddr.value = officeAddrFromText(s);
    lastFormattedAddress = s;
  }
});

// ── 자동채움: 회사 선택 → 회사명·등록번호·이사(서명자) ──
async function autofillFromEntity(cdRaw: string) {
  const cd = t(cdRaw);
  if (!cd || cd === lastAutofillCd.value) return;
  const entityChanged = !!lastAutofillCd.value && lastAutofillCd.value !== cd;
  lastAutofillCd.value = cd;
  for (const k of autofilledKeys.value) fill.setValue(k, '');
  if (entityChanged) signersCtx.clearAll();
  const written = new Set<string>();
  const set = (k: string, v: unknown) => { const s = t(v); if (s) { fill.setValue(k, s); written.add(k); } };

  set('company_name', props.chatFormData?.entityName || props.entityData?.companyName);
  set('company_registration_no', props.chatFormData?.registrationNo || props.entityData?.registrationNo);
  try {
    const res: any = await fetch(`/api/entity/${encodeURIComponent(cd)}/agm-source`, { credentials: 'include' }).then((r) => r.json());
    const src: any = res?.data?.data ?? res?.data ?? res ?? {};
    set('company_name', src.companyName);
    set('company_registration_no', src.registrationNo);
    // 변경 전 주소 = 지금 엔티티에 등록된 등록사무소 주소(문서 "changed from … to …")
    set('old_office_address', src.registeredOfficeAddress);
  } catch (e) { console.warn('[ChangeAddress] agm-source failed', e); }

  await seedDirectorSigners(cd);
  autofilledKeys.value = written;
  emitProgress();
}

async function backfillOldAddress(cd: string) {
  if (!cd) return;
  try {
    const res: any = await fetch(`/api/entity/${encodeURIComponent(cd)}/agm-source`, { credentials: 'include' }).then((r) => r.json());
    const src: any = res?.data?.data ?? res?.data ?? res ?? {};
    if (!t(fill.values.old_office_address) && t(src.registeredOfficeAddress)) fill.setValue('old_office_address', t(src.registeredOfficeAddress));
  } catch { /* ignore */ }
}

/** 이사 전원 = DR 서명자. 이미 서명자가 있으면 건드리지 않는다. */
async function seedDirectorSigners(cd: string) {
  if (!cd || signersCtx.signersOf('dr_Signers').length) return;
  try {
    const res: any = await docGenApi.getEntityOfficersAll(cd);
    const officers: any[] = res?.data?.officers ?? res?.data?.data?.officers ?? [];
    const names: string[] = [];
    for (const o of officers) {
      const positions: any[] = Array.isArray(o?.positions) ? o.positions : [];
      const isDir = positions.some((p) => String(p?.positionCd || '') === 'B30027' || /director/i.test(String(p?.positionName || '')));
      const nm = t(o?.contactName || o?.name || o?.fullName);
      if (isDir && nm && !names.includes(nm)) names.push(nm);
    }
    if (names.length && !signersCtx.signersOf('dr_Signers').length) {
      signersCtx.setSigners('dr_Signers', names.map(mkSigner));
    }
  } catch (e) { console.warn('[ChangeAddress] officers-by-entity-all failed', e); }
}

watch(() => props.chatFormData, (fd) => {
  const cd = t(fd?.entityCd || fd?.icpCd);
  if (cd && !editMode.value) void autofillFromEntity(cd);
}, { deep: true });
watch(entityCd, (cd) => { if (cd && !editMode.value) void autofillFromEntity(cd); }, { immediate: true });

onMounted(async () => {
  emitProgress();
  try {
    const cfg: any = await getTaskConfigByCode(t(props.selection.task.TASK_CODE));
    taskConfigSteps.value = Array.isArray(cfg?.processSteps) ? cfg.processSteps : [];
  } catch { /* ignore */ }
});

// ── Edit 지원 ──
function gatherState() {
  return {
    values: { ...fill.exportPayload() },
    signers: signersCtx.exportAll(),
    officeAddr: { ...officeAddr.value },
    entityCd: entityCd.value,
  };
}
function restoreState(s: any) {
  if (!s) return;
  // 이전(비스튜디오) 패널로 만든 task: { formModel: { companyName, companyRegistrationNo, officeAddr, dated } }
  if (s.formModel && typeof s.formModel === 'object') {
    const m = s.formModel;
    if (m.companyName) fill.setValue('company_name', String(m.companyName));
    if (m.companyRegistrationNo) fill.setValue('company_registration_no', String(m.companyRegistrationNo));
    if (m.dated) fill.setValue('resolution_date', String(m.dated));
    if (m.officeAddr) setOfficeAddr({ ...createEmptyOfficeAddr(), ...m.officeAddr });
  }
  if (s.values && typeof s.values === 'object') {
    for (const [k, v] of Object.entries(s.values)) if (v != null && k !== ADDRESS_KEY) fill.setValue(k, String(v));
  }
  if (s.officeAddr && typeof s.officeAddr === 'object') setOfficeAddr({ ...createEmptyOfficeAddr(), ...s.officeAddr });
  else if (s.values?.[ADDRESS_KEY]) fill.setValue(ADDRESS_KEY, String(s.values[ADDRESS_KEY]));
  if (s.signers) signersCtx.importAll(s.signers);
  // 이전 패널로 만든 task 는 서명자 스냅샷이 없다 → 이사 목록으로 채움.
  void seedDirectorSigners(t(s.entityCd) || entityCd.value);
  // 변경 전 주소 칸이 생기기 전에 만든 task — 저장값이 없으면 엔티티 현재 등록사무소 주소로 채움.
  if (!t(fill.values.old_office_address)) void backfillOldAddress(t(s.entityCd) || entityCd.value);
  emitProgress();
}

// ── Create / Update ──
async function createTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) { progress.value = 'Please enter a Task Title.'; return; }
  if (!entityCd.value) { progress.value = 'Please select a company first.'; return; }
  const v = fill.exportPayload();
  const companyName = t(v.company_name);
  const regNo = t(v.company_registration_no);
  const newAddress = t(v[ADDRESS_KEY]);
  const dated = t(v.resolution_date);
  if (!companyName || !regNo || !newAddress || !dated) {
    progress.value = 'Please fill in the company, new registered office address and date.';
    return;
  }

  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const taskTypeCd = t(props.selection.task.TASK_CODE) || TASK_CODE;
    const model = { companyName, companyRegistrationNo: regNo, officeAddr: { ...officeAddr.value }, dated };
    // TASK_DATA — e-Filing 이 읽는 기존 키(newOfficeAddress / officeAddr / dated 등) 유지.
    const taskFormData = {
      ...buildChangeAddressTaskFormData(model, entityCd.value),
      newOfficeAddress: newAddress,
      oldOfficeAddress: t(v.old_office_address),
      presetForm: gatherState(),
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id'; return; }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: taskFormData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value,
          taskTypeCd,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: effectiveTitle.value,
          taskFlow: {
            createDocument: steps.length === 0 ? true : steps.includes('doc'),
            requestTSign: steps.includes('t-sign'),
            proceedWithEFiling: steps.includes('e-filing') || steps.includes('efiling'),
          },
          officerData: taskFormData,
        }),
      }).then((r) => r.json());
      taskId = Number(taskRes?.data?.taskId || taskRes?.taskId || 0) || null;
      if (!taskId) { progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`; return; }
    }

    progress.value = 'Saving documents…';
    const boardSigners = signersCtx.signersOf('dr_Signers');
    const docFormData: Record<string, any> = {
      company_name: companyName,
      company_registration_no: regNo,
      old_office_address: t(v.old_office_address), // 변경 전 주소(템플릿 [[old_office_address]])
      new_office_address: newAddress,
      dated,
    };

    // L_TASK_EFILING_DATA 초기 저장 — e-Filing 화면 prefill 용(기존 패널과 동일 형식).
    try {
      await saveEFilingData(taskId, {
        taskTypeCd,
        entityCd: entityCd.value,
        countryCd: 'SG',
        formData: {
          ...docFormData,
          companyName,
          companyRegistrationNo: regNo,
          officeAddr: { ...officeAddr.value },
        } as Record<string, unknown>,
        requestPayload: null,
        responseData: null,
        status: 'B36002',
        isSuccess: false,
      });
    } catch (e) { console.warn('[ChangeAddress] L_TASK_EFILING_DATA initial save failed', e); }

    const seen = new Set<number>();
    const docsToSave = (props.selection.documents || [])
      .filter((d: any) => {
        const tid = Number(d?.tmpId || 0);
        if (!tid || seen.has(tid)) return false;
        seen.add(tid); return true;
      })
      .map((d: any) => ({
        templateId: Number(d.tmpId),
        label: String(d.templateName || d.docCode || 'Document').replace(/\.docx?$/i, '').trim(),
        personName: companyName,
        formData: { ...docFormData },
        signers: boardSigners.length ? boardSigners : undefined,
        entityCd: entityCd.value,
      }));
    if (docsToSave.length) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        }).then((r) => r.json());
        if (!saveRes?.success) progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`;
      } catch (err: any) { progress.value = `⚠ Document save failed: ${err?.message || err}`; }
    }
    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
    } catch { /* ignore */ }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ presetTaskId: props.selection.task.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    progress.value = e?.message || 'Create failed';
  } finally {
    creating.value = false;
  }
}

defineExpose({ gatherState, restoreState, createTask });
</script>

<template>
  <div class="ca-panel" :class="{ studio }">
    <div v-if="!studio" class="co-head">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME || 'Change of Registered Office Address' }}</div>
        <div class="co-subtitle">Directors' Resolution — Change of Registered Office Address (Singapore)</div>
      </div>
      <div class="co-actions">
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-task-title')">Change title</v-btn>
        <v-btn variant="text" size="small" class="text-none" @click="emit('change-entity')">Change company</v-btn>
      </div>
    </div>

    <div class="co-doc">
      <ChangeAddressPackageDoc />
    </div>

    <div v-if="!studio" class="co-foot">
      <v-btn color="primary" class="text-none" :loading="creating" :disabled="!effectiveTitle || !isReady || creating" @click="createTask">
        {{ editMode ? 'Update task' : 'Create Task' }}
      </v-btn>
      <span v-if="progress && !creating" class="co-err">{{ progress }}</span>
    </div>
    <div v-else-if="progress && !creating" class="co-foot">
      <span class="co-err">{{ progress }}</span>
    </div>
  </div>
</template>

<style scoped>
.ca-panel { display: flex; flex-direction: column; gap: 12px; min-height: 0; }
.ca-panel.studio .co-foot { position: sticky; bottom: 0; background: #fff; padding: 10px 0; z-index: 2; }
.co-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.co-title { font-size: 16px; font-weight: 800; color: #1a2336; }
.co-subtitle { font-size: 12px; color: #6b7a94; margin-top: 2px; }
.co-actions { display: flex; gap: 4px; flex-shrink: 0; }
.co-doc { flex: 1; min-height: 0; }
.co-foot { display: flex; align-items: center; gap: 12px; }
.co-err { font-size: 12px; color: #c0392b; }
</style>
